import { nanoid } from '@/utils/helper';

export interface IStat {
    label: string;
    value: number | string;
}

interface StatsCardProps {
    stats: IStat[];
}

const StatsCard: React.FC<StatsCardProps> = ({ stats }) => {
    // Circle geometry
    const RADIUS = 30;
    const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

    // Function to clamp a value between 0 and 100
    const clamp = (val: number) => Math.max(0, Math.min(val, 100));

    // Compute strokeDashoffset for a given value percentage
    const getStrokeDashOffset = (value: number) => {
        const clampedValue = clamp(value);
        return CIRCUMFERENCE - (clampedValue / 100) * CIRCUMFERENCE;
    };

    // Main progress rendering helper:
    // If a single value is provided, we render one arc.
    // If a range is provided (e.g. "7-10"), we render two arcs.
    const renderArcs = (value: number | string) => {
        // Default color (for the first segment)
        const lightColor = '#cf9424'; // original color
        // Darker color for the second segment (you can adjust this to your desired darker shade)
        const darkColor = '#a16d1e';

        // If value is a number (or a numeric string without a hyphen) render one arc
        if (typeof value === 'number' || (typeof value === 'string' && !value.includes('-'))) {
            const numericValue = typeof value === 'number' ? value : parseFloat(value);
            return (
                <svg
                    className="absolute top-0 left-0 w-full h-full transform -rotate-90"
                    width="100%"
                    height="100%"
                >
                    <circle
                        cx="50%"
                        cy="50%"
                        r={RADIUS}
                        stroke={lightColor}
                        strokeWidth="6"
                        fill="transparent"
                        strokeDasharray={CIRCUMFERENCE}
                        strokeDashoffset={getStrokeDashOffset(numericValue)}
                        strokeLinecap="round"
                    />
                </svg>
            );
        }

        // Otherwise, if value is a string containing a hyphen, parse the range
        const parts = value.split('-');
        if (parts.length === 2) {
            const lower = parseFloat(parts[0]);
            const upper = parseFloat(parts[1]);
            // Ensure our values are clamped within 0-100
            const clampedLower = clamp(lower);
            const clampedUpper = clamp(upper);

            return (
                <>
                    {/* Dark arc for the entire range from 0 to upper */}
                    <svg
                        className="absolute top-0 left-0 w-full h-full transform -rotate-90"
                        width="100%"
                        height="100%"
                    >
                        <circle
                            cx="50%"
                            cy="50%"
                            r={RADIUS}
                            stroke={darkColor}
                            strokeWidth="6"
                            fill="transparent"
                            strokeDasharray={CIRCUMFERENCE}
                            strokeDashoffset={getStrokeDashOffset(clampedUpper)}
                            strokeLinecap="round"
                        />
                    </svg>
                    {/* Light arc over the lower portion from 0 to lower */}
                    <svg
                        className="absolute top-0 left-0 w-full h-full transform -rotate-90"
                        width="100%"
                        height="100%"
                    >
                        <circle
                            cx="50%"
                            cy="50%"
                            r={RADIUS}
                            stroke={lightColor}
                            strokeWidth="6"
                            fill="transparent"
                            strokeDasharray={CIRCUMFERENCE}
                            strokeDashoffset={getStrokeDashOffset(clampedLower)}
                            strokeLinecap="round"
                        />
                    </svg>
                </>
            );
        }

        // Fallback: if parsing fails, simply render nothing
        return null;
    };

    return (
        <div className="max-w-xl mx-auto">
            <div className="flex flex-wrap gap-6 justify-center mt-6">
                {stats.map(stat => (
                    <div
                        key={nanoid()}
                        className="flex flex-col items-center justify-center"
                    >
                        {/* Ring Container */}
                        <div className="relative w-20 h-20">
                            {/* Numeric Value in the center */}
                            <div
                                style={{
                                    color: '#085a5c',
                                    fontWeight: 'bolder',
                                }}
                                className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-lg"
                            >
                                {stat.value}%
                            </div>

                            {/* Background circle (gray track) */}
                            <svg
                                className="transform -rotate-90"
                                width="100%"
                                height="100%"
                            >
                                <circle
                                    cx="50%"
                                    cy="50%"
                                    r={RADIUS}
                                    stroke="#e2e8f0" /* Tailwind gray-200 */
                                    strokeWidth="6"
                                    fill="transparent"
                                />
                            </svg>

                            {/* Foreground arc(s) */}
                            {renderArcs(stat.value)}
                        </div>

                        {/* Label */}
                        <div
                            style={{
                                color: '#085a5c',
                                fontWeight: 'bolder',
                            }}
                            className="mt-3 font-medium text-sm text-center"
                        >
                            {stat.label}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default StatsCard;
